import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

import TituloAdmin from '@components/TituloAdmin';
import BotonIconoTabla from '@components/BotonIconoTabla';
import Boton from '@components/Boton';
import Label from '@components/Label';
import Input from '@components/Input';
import { cajaPrincipalStyle } from '@styles/cajaPrincipal';
import { cajaContenedorAdminStyle } from '@styles/cajaContenedorAdmin';
import { tablaEstylos, tablaTheadEstylos, thStyle, trStyle } from '@styles/tablaEstylos';
import { colors } from '@styles/colors';
import { selectStyle } from '@styles/selecsEstylos';
import { getAllSedesApi, viewSedesApi, updateSedeApi, updateSedeSpecialtiesApi } from '@api/wp/sedes/sedesApi';
import { getAllEspecialidadesApi } from '@api/wp/especialidades/especialidadesApi';
import { getAllOficinasEspecialidadesSedesApi } from '@api/wp/especialidadesSedes/especialidadesSedesApi';
import '@styles/select2Custom.css';
import Notify from '@components/Notify';
import LoadingOverlay from '@components/LoadingOverlay';
import LabelCarga from '@components/LabelCarga';
import '@styles/modernTable.css';

const SpecialtySelect = ({ initialValue, options, onSave }) => {
  const selectRef = useRef(null);
  const [selectedData, setSelectedData] = useState([]);
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    const $ = window.$;
    if (!$?.fn?.select2) {
      return undefined;
    }
    const el = selectRef.current;
    if (!el) {
      return undefined;
    }

    let cancelled = false;
    let syncTimeoutId = null;
    const $el = $(el);

    let selectedIds = [];
    if (initialValue) {
      const values = initialValue.split(',').map((v) => v.trim().toLowerCase());
      values.forEach((v) => {
        if (!v) return;
        const exact = options.find((o) => o.label.toLowerCase().trim() === v);
        if (exact) {
          selectedIds.push(exact.value);
        } else {
          const partial = options.find((o) => o.label.toLowerCase().startsWith(`${v} -`));
          if (partial) selectedIds.push(partial.value);
        }
      });
    }

    try {
      if ($el.hasClass('select2-hidden-accessible') || $el.data('select2')) {
        $el.off('change.specialtyselect');
        $el.select2('destroy');
      }

      $el.select2({
        width: '100%',
        minimumResultsForSearch: 0,
        placeholder: '-- Seleccione --',
        allowClear: true,
      });

      if (selectedIds.length > 0) {
        $el.val(selectedIds).trigger('change.select2');
        syncTimeoutId = window.setTimeout(() => {
          if (cancelled || !$el.data('select2')) return;
          setSelectedData($el.select2('data'));
        }, 0);
      }

      $el.on('change.specialtyselect', () => {
        if (cancelled || !$el.data('select2')) return;
        const data = $el.select2('data');
        setSelectedData(data);
        setHasChanged(true);
      });
    } catch (e) {
      console.error('Select2 (SpecialtySelect):', e);
    }

    return () => {
      cancelled = true;
      if (syncTimeoutId != null) {
        window.clearTimeout(syncTimeoutId);
      }
      try {
        const $jq = window.$;
        if (!$jq?.fn?.select2) return;
        const $cleanup = $jq(el);
        $cleanup.off('change.specialtyselect');
        if ($cleanup.data('select2') || $cleanup.hasClass('select2-hidden-accessible')) {
          $cleanup.select2('destroy');
        }
      } catch (_) {
        /* nodo ya retirado o Select2 en estado inconsistente al cambiar de ruta */
      }
    };
  }, [options, initialValue]);

  const handleSave = () => {
    onSave(selectedData);
    setHasChanged(false);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ flex: 1 }}>
        <select ref={selectRef} style={{ width: '100%' }} multiple>
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      {hasChanged && (
        <IconButton
          onClick={handleSave}
          size="small"
          style={{ backgroundColor: colors.primary, color: 'white', padding: '4px' }}
          title="Guardar cambios"
        >
          <CheckIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};

function Sedes() {
  const navigate = useNavigate();
  const [sedes, setSedes] = useState([]);
  const [especialidadesOptions, setEspecialidadesOptions] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
  const [busqueda, setBusqueda] = useState('');
  const [filtroEspecialidades, setFiltroEspecialidades] = useState('todas');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalUnusedOpen, setModalUnusedOpen] = useState(false);
  const [unusedSpecialties, setUnusedSpecialties] = useState([]);
  const [unusedSearch, setUnusedSearch] = useState('');

  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ nombre_sede: '', direccion: '' });
  const filtroEspSelectRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  const cargarSedes = async () => {
    try {
      setCargando(true);
      
      const [sedesResp, espResp] = await Promise.all([
        getAllSedesApi(),
        getAllEspecialidadesApi()
      ]);

      const lista = sedesResp?.result || sedesResp?.data || [];
      const arr = Array.isArray(lista) ? lista : [];
      setSedes(arr);

      const listaEsp = espResp?.result || espResp?.data || [];
      const options = Array.isArray(listaEsp) 
        ? listaEsp.map(e => ({ value: e.id, label: `${e.nombre_especialidad} - ${e.nombre_empresa}` }))
        : [];
      setEspecialidadesOptions(options);

    } catch (err) {
      console.error('Error al cargar datos:', err);
      setNotification({ open: true, message: 'No se pudieron cargar las sedes o especialidades', type: 'error' });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarSedes();
  }, []);

  // Select2 en filtro "Filtrar por Especialidades" (mismo criterio que Consultas / Programar)
  useEffect(() => {
    if (cargando) return undefined;

    const $ = window.$;
    if (!$?.fn?.select2 || !filtroEspSelectRef.current) {
      return undefined;
    }

    const el = filtroEspSelectRef.current;
    const $el = $(el);

    try {
      if ($el.hasClass('select2-hidden-accessible') || $el.data('select2')) {
        $el.off('change.select2react');
        $el.select2('destroy');
      }

      $el.select2({
        placeholder: '-- Filtrar por especialidades --',
        allowClear: false,
        width: '100%',
        theme: 'default',
        minimumResultsForSearch: 0,
        language: {
          noResults: function () { return 'No se encontraron resultados'; },
          searching: function () { return 'Buscando...'; },
        },
      });

      $el.on('change.select2react', () => {
        const value = $el.val() || 'todas';
        setFiltroEspecialidades(value);
      });

      $el.val(String(filtroEspecialidades)).trigger('change');
    } catch (e) {
      console.error('Error inicializando Select2 (filtro especialidades):', e);
    }

    return () => {
      try {
        const $jq = window.$;
        if (!$jq?.fn?.select2) return;
        const $cleanup = $jq(el);
        $cleanup.off('change.select2react');
        if ($cleanup.data('select2') || $cleanup.hasClass('select2-hidden-accessible')) {
          $cleanup.select2('destroy');
        }
      } catch (_) {
        /* ignore */
      }
    };
  }, [cargando, especialidadesOptions.length]);

  const handleCheckUnused = async () => {
    try {
      setProcesando(true);
      const resp = await getAllOficinasEspecialidadesSedesApi();
      const relaciones = resp?.result || resp?.data || [];
      // Assuming 'idEsp' is the field for specialty ID in the relation
      const usedIds = new Set(relaciones.map(r => Number(r.idEsp)));
      
      const unused = especialidadesOptions.filter(opt => !usedIds.has(Number(opt.value)));
      setUnusedSpecialties(unused);
      setUnusedSearch('');
      setModalUnusedOpen(true);
    } catch (err) {
      console.error('Error checking unused:', err);
      setNotification({ open: true, message: 'Error al verificar especialidades', type: 'error' });
    } finally {
      setProcesando(false);
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({
      nombre_sede: row.nombre_sede || '',
      direccion: row.direccion || ''
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!editId) return;
    try {
      setProcesando(true);
      const payload = {
        nombre_sede: form.nombre_sede || '',
        direccion: form.direccion || ''
      };
      await updateSedeApi(editId, payload);
      setSedes((prev) => prev.map((s) => (s.id === editId ? { ...s, ...payload } : s)));
      setNotification({ open: true, message: 'Sede actualizada correctamente', type: 'success' });
      setModalOpen(false);
      setEditId(null);
    } catch (err) {
      console.error('Error al actualizar sede:', err);
      setNotification({ open: true, message: 'Error al actualizar sede', type: 'error' });
    } finally {
      setProcesando(false);
    }
  };

  const handleSpecialtySave = async (idSed, selectedData) => {
    try {
      setProcesando(true);
      const specialtyIds = selectedData.map(d => d.id);
      await updateSedeSpecialtiesApi(idSed, specialtyIds);
      
      // Update local state to reflect changes
      const newSpecialtiesStr = selectedData.map(d => {
          // select2 data objects have 'text' property for label, but our options have 'label'. 
          // However, we initialized select2 with options having 'text' implied by 'option' tag content.
          // Wait, Select2 data objects usually have 'id' and 'text'.
          // Let's verify what options map produces.
          // options map produces { value, label }. 
          // <option> uses value and label.
          // Select2 data uses 'id' (value) and 'text' (label).
          return d.text; 
      }).join(', ');

      setSedes(prev => prev.map(s => s.id === idSed ? { ...s, especialidades: newSpecialtiesStr } : s));
      
      setNotification({ open: true, message: 'Especialidades actualizadas correctamente', type: 'success' });
    } catch (err) {
      console.error('Error saving specialties:', err);
      setNotification({ open: true, message: 'Error al guardar especialidades', type: 'error' });
    } finally {
      setProcesando(false);
    }
  };

  const descargarExcel = () => {
    try {
      const headers = ['Nombre Sede', 'OID', 'Dirección', 'Especialidades'];
      const datos = Array.isArray(sedes) ? sedes : [];

      const filas = datos.map((item) => [
        item?.nombre_sede ?? 'N/A',
        item?.oid ?? 'N/A',
        item?.direccion ?? 'N/A',
        item?.especialidades ?? 'N/A'
      ]);

      const sep = ';';
      const esc = (val) => {
        const s = String(val).replace(/"/g, '""');
        return `"${s}"`;
      };

      let contenido = '\uFEFF';
      contenido += headers.map(esc).join(sep) + '\r\n';
      for (const fila of filas) {
        contenido += fila.map(esc).join(sep) + '\r\n';
      }

      const blob = new Blob([contenido], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const fecha = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `sedes_${fecha}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error al exportar CSV:', e);
      setNotification({ open: true, message: 'Error al exportar', type: 'error' });
    }
  };

  return (
    <div id="cajaPrincipal" style={cajaPrincipalStyle}>
      <LoadingOverlay visible={procesando} />
      <TituloAdmin
        titulo="Sedes"
        imagen_titulo="servicios"
        subtitulo="Listado y edición de credenciales Bold por sedes"
      />

      <div style={cajaContenedorAdminStyle}>
        {cargando ? (
          <LabelCarga nombre="sedes" />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2vh' }}>
          <div style={{ display: 'flex', gap: '2vh', alignItems: 'center' }}>
            <div>
              <Label>Buscar Sede</Label>
              <Input
                placeholder='Buscar por nombre...'
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                ancho='30vh'
                icono='buscar'
              />
            </div>
            <div style={{ minWidth: '25vh', maxWidth: '100%', flex: '0 1 32vh' }}>
              <Label>Filtrar por Especialidades</Label>
              <select
                ref={filtroEspSelectRef}
                value={filtroEspecialidades}
                onChange={(e) => setFiltroEspecialidades(e.target.value)}
                style={selectStyle}
              >
                <option value="todas">Ver Todas</option>
                <option value="sin_especialidades">Sin Especialidades</option>
              </select>
            </div>
          </div>
          <Boton
            label="Ver Esp. Sin Sede"
            onClick={handleCheckUnused}
            color={colors.secondaryButton}
            ancho="22vh"
            alto="5vh"
          />
        </div>
        <div className="modern-table-container">
          <table id="tabla-sedes" className="modern-table">
            <thead>
              <tr>
                <th>Nombre Sede</th>
                <th>OID</th>
                <th>Dirección</th>
                <th style={{ width: '30%' }}>Especialidades</th>
                <th style={{ textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sedes.filter(s => !busqueda || s.nombre_sede?.toLowerCase().includes(busqueda.toLowerCase())).map((row, index) => (
                <tr key={row.id}>
                  <td>{row.nombre_sede || ''}</td>
                  <td>{row.oid || ''}</td>
                  <td>{row.direccion || ''}</td>
                  <td>
                    <SpecialtySelect
                      key={row.id}
                      initialValue={row.especialidades}
                      options={especialidadesOptions}
                      onSave={(data) => handleSpecialtySave(row.id, data)}
                    />
                  </td>
                  <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <BotonIconoTabla
                        icono="edit"
                        tooltipText="Editar"
                        onClick={() => handleEdit(row)}
                        disabled={procesando}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ textAlign: 'right', marginTop: '1.5vh' }}>
                <Boton
                  label={"Descargar Excel"}
                  onClick={descargarExcel}
                  disabled={cargando || !Array.isArray(sedes) || sedes.length === 0}
                  color={colors.primary}
                  ancho="25vh"
                  alto="5vh"
                />
              </div>
          </>
        )}
      </div>
    {/* Modal de edición */}
    <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle style={{ fontFamily: 'Inter', fontWeight: 700 }}>Editar Sede</DialogTitle>
      <DialogContent dividers>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.6vh' }}>
          <TextField
            fullWidth
            label="Nombre Sede"
            value={form.nombre_sede}
            onChange={(e) => setForm((prev) => ({ ...prev, nombre_sede: e.target.value }))}
            InputLabelProps={{ style: { fontFamily: 'Inter' } }}
            inputProps={{ style: { fontFamily: 'Inter' } }}
          />

          <TextField
            fullWidth
            label="Dirección"
            value={form.direccion}
            onChange={(e) => setForm((prev) => ({ ...prev, direccion: e.target.value }))}
            InputLabelProps={{ style: { fontFamily: 'Inter' } }}
            inputProps={{ style: { fontFamily: 'Inter' } }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <button
          onClick={() => setModalOpen(false)}
          style={{
            backgroundColor: colors.gray200,
            color: colors.primaryText,
            border: 'none',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontSize: '1.6vh',
            fontFamily: 'Inter',
            cursor: 'pointer'
          }}
        >
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={procesando}
          style={{
            backgroundColor: colors.primary,
            color: colors.white,
            border: 'none',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontSize: '1.6vh',
            fontFamily: 'Inter',
            fontWeight: '600',
            cursor: procesando ? 'not-allowed' : 'pointer'
          }}
        >
          {procesando ? 'Guardando...' : 'Guardar'}
        </button>
      </DialogActions>
    </Dialog>

    {/* Modal de Especialidades Sin Sede */}
    <Dialog open={modalUnusedOpen} onClose={() => setModalUnusedOpen(false)} fullWidth maxWidth="md">
      <DialogTitle style={{ fontFamily: 'Inter', fontWeight: 700 }}>Especialidades sin Sede Asignada</DialogTitle>
      <DialogContent dividers>
        <div style={{ marginBottom: '2vh' }}>
          <Label>Buscar Especialidad</Label>
          <Input
            placeholder='Buscar...'
            value={unusedSearch}
            onChange={(e) => setUnusedSearch(e.target.value)}
            ancho='100%'
            icono='buscar'
          />
        </div>
        <div className="modern-table-container" style={{ maxHeight: '50vh' }}>
          {unusedSpecialties.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2vh', fontFamily: 'Inter', color: colors.gray700 }}>
              Todas las especialidades tienen al menos una sede asignada.
            </div>
          ) : (
            <table className="modern-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre Especialidad - Empresa</th>
                </tr>
              </thead>
              <tbody>
                {unusedSpecialties
                  .filter(esp => 
                    !unusedSearch || 
                    esp.label.toLowerCase().includes(unusedSearch.toLowerCase())
                  )
                  .map((esp, index) => (
                    <tr key={esp.value}>
                      <td style={{ fontWeight: 'normal' }}>{esp.value}</td>
                      <td style={{ fontWeight: 'normal' }}>{esp.label}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <button
          onClick={() => setModalUnusedOpen(false)}
          style={{
            backgroundColor: colors.primary,
            color: colors.white,
            border: 'none',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontSize: '1.6vh',
            fontFamily: 'Inter',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Cerrar
        </button>
      </DialogActions>
    </Dialog>

    <Notify
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={handleCloseNotification}
      />
    <LoadingOverlay visible={cargando || procesando} />
    </div>
  );
}

export default Sedes ;



